import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Inventory2 as AssetsIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as DepIcon,
  AccountBalance as NBVIcon,
  FilterList as FilterIcon,
  PlayArrow as GenerateIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import ReportHeader from '../../components/Reports/ReportHeader';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { AssetRegisterRow, AssetType, AssetStatus, DepreciationMethod } from '../../types/fixedAssets.types';

// ── Translations ───────────────────────────────────────────────────────────────

const translations = {
  en: {
    title: 'Fixed Assets Register',
    titleAr: 'سجل الأصول الثابتة',
    subtitle: 'Asset register report with acquisition cost, accumulated depreciation and net book value',
    filters: 'Filters',
    asOfDate: 'As of Date',
    assetType: 'Asset Type',
    status: 'Status',
    all: 'All',
    tangible: 'Tangible',
    intangible: 'Intangible',
    active: 'Active',
    fullyDepreciated: 'Fully Depreciated',
    disposed: 'Disposed',
    generateReport: 'Generate Report',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    assetCode: 'Asset Code',
    assetName: 'Asset Name',
    type: 'Type',
    category: 'Category',
    acquisitionDate: 'Acq. Date',
    acquisitionCost: 'Acq. Cost',
    accumDepreciation: 'Accum. Depr.',
    netBookValue: 'Net Book Value',
    usefulLife: 'Useful Life',
    method: 'Method',
    statusCol: 'Status',
    straightLine: 'Straight-Line',
    decliningBalance: 'Declining Balance',
    years: 'yrs',
    kwd: 'KWD',
    loading: 'Loading...',
    noData: 'No assets found. Adjust filters and click Generate Report.',
    rowsPerPage: 'Rows per page:',
    totalAssets: 'Total Assets',
    totalAcquisitionCost: 'Total Acquisition Cost',
    totalAccumDepreciation: 'Total Accum. Depreciation',
    totalNetBookValue: 'Total Net Book Value',
    grandTotal: 'Grand Total',
    generatedOn: 'Generated:',
    asOf: 'As of:',
    telephone: 'Tel:',
    email: 'Email:',
  },
  ar: {
    title: 'Fixed Assets Register',
    titleAr: 'سجل الأصول الثابتة',
    subtitle: 'تقرير سجل الأصول مع تكلفة الاقتناء، مجمع الاستهلاك، وصافي القيمة الدفترية',
    filters: 'الفلاتر',
    asOfDate: 'حتى تاريخ',
    assetType: 'نوع الأصل',
    status: 'الحالة',
    all: 'الكل',
    tangible: 'ملموس',
    intangible: 'غير ملموس',
    active: 'نشط',
    fullyDepreciated: 'مستهلك بالكامل',
    disposed: 'مُستبعد',
    generateReport: 'إنشاء التقرير',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    assetCode: 'كود الأصل',
    assetName: 'اسم الأصل',
    type: 'النوع',
    category: 'الفئة',
    acquisitionDate: 'تاريخ الاقتناء',
    acquisitionCost: 'تكلفة الاقتناء',
    accumDepreciation: 'مجمع الاستهلاك',
    netBookValue: 'صافي القيمة الدفترية',
    usefulLife: 'العمر الإنتاجي',
    method: 'الطريقة',
    statusCol: 'الحالة',
    straightLine: 'القسط الثابت',
    decliningBalance: 'القسط المتناقص',
    years: 'سنة',
    kwd: 'د.ك',
    loading: 'جاري التحميل...',
    noData: 'لا توجد أصول. عدّل الفلاتر وانقر إنشاء التقرير.',
    rowsPerPage: 'عدد الصفوف:',
    totalAssets: 'إجمالي الأصول',
    totalAcquisitionCost: 'إجمالي تكلفة الاقتناء',
    totalAccumDepreciation: 'إجمالي مجمع الاستهلاك',
    totalNetBookValue: 'إجمالي صافي القيمة الدفترية',
    grandTotal: 'الإجمالي العام',
    generatedOn: 'تاريخ الإنشاء:',
    asOf: 'حتى تاريخ:',
    telephone: 'هاتف:',
    email: 'بريد:',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const todayIso = (): string => new Date().toISOString().split('T')[0];

const formatNumber = (num: number | null | undefined, decimals = 3): string => {
  if (num == null || isNaN(Number(num))) return '0.000';
  return Number(num).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatDate = (iso: string, locale: string): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(locale);
};

// ── Component ──────────────────────────────────────────────────────────────────

const FixedAssetsReportPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const locale = isRTL ? 'ar-KW' : 'en-GB';

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [data, setData] = useState<AssetRegisterRow[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Filters
  const [asOfDate, setAsOfDate] = useState<string>(todayIso());
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetType | ''>('');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | ''>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Derived stats (computed over ALL filtered rows, not just current page)
  const filteredData = React.useMemo(() => {
    return data.filter(row => {
      if (assetTypeFilter && row.assetType !== assetTypeFilter) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      return true;
    });
  }, [data, assetTypeFilter, statusFilter]);

  const stats = React.useMemo(() => ({
    totalAssets: filteredData.length,
    totalAcquisitionCost: filteredData.reduce((s, r) => s + r.acquisitionCost, 0),
    totalAccumDepreciation: filteredData.reduce((s, r) => s + r.accumulatedDepreciation, 0),
    totalNetBookValue: filteredData.reduce((s, r) => s + r.netBookValue, 0),
  }), [filteredData]);

  const pagedData = React.useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(console.error);
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setLoading(true);
    setHasGenerated(false);
    try {
      const rows = await fixedAssetsService.getAssetRegister(asOfDate || undefined);
      setData(rows);
      setPage(0);
      setHasGenerated(true);
    } catch (err) {
      console.error('Failed to load asset register:', err);
      toast.error(isRTL ? 'فشل تحميل التقرير' : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // ── Label helpers ──────────────────────────────────────────────────────────
  const assetTypeLabelEn = (type: AssetType): string =>
    type === 'Tangible' ? 'Tangible' : 'Intangible';

  const assetTypeLabel = (type: AssetType): string =>
    isRTL
      ? type === 'Tangible' ? 'ملموس' : 'غير ملموس'
      : assetTypeLabelEn(type);

  const methodLabelEn = (m: DepreciationMethod): string =>
    m === 'StraightLine' ? 'Straight-Line' : 'Declining Balance';

  const methodLabel = (m: DepreciationMethod): string =>
    isRTL
      ? m === 'StraightLine' ? 'القسط الثابت' : 'القسط المتناقص'
      : methodLabelEn(m);

  const statusLabelEn = (s: AssetStatus): string => {
    if (s === 'Active') return 'Active';
    if (s === 'FullyDepreciated') return 'Fully Depreciated';
    return 'Disposed';
  };

  const statusLabel = (s: AssetStatus): string => {
    if (isRTL) {
      if (s === 'Active') return 'نشط';
      if (s === 'FullyDepreciated') return 'مستهلك بالكامل';
      return 'مُستبعد';
    }
    return statusLabelEn(s);
  };

  const statusColor = (s: AssetStatus): 'success' | 'default' | 'error' => {
    if (s === 'Active') return 'success';
    if (s === 'FullyDepreciated') return 'default';
    return 'error';
  };

  const typeColor = (type: AssetType): 'primary' | 'secondary' =>
    type === 'Tangible' ? 'primary' : 'secondary';

  // ── Excel Export ───────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    if (!hasGenerated || filteredData.length === 0) {
      toast.warning(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName = isRTL ? 'سجل الأصول الثابتة' : 'Fixed Assets Register';
      const worksheet = workbook.addWorksheet(sheetName);

      const COL_COUNT = 11;
      const lastCol = String.fromCharCode(64 + COL_COUNT); // 'K'

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          const base64Data = companyInfo.logoBase64.split(',')[1] ?? companyInfo.logoBase64;
          const imageId = workbook.addImage({ base64: base64Data, extension: 'png' });
          worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch (err) {
          console.error('Excel: Failed to add logo:', err);
        }
      }

      // Company name — Row 1
      worksheet.mergeCells(`C1:${lastCol}1`);
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 30;

      // Address — Row 2
      worksheet.mergeCells(`C2:${lastCol}2`);
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 11, color: { argb: 'FF666666' } };
      addressCell.alignment = { horizontal: 'center' };

      // Contact — Row 3
      worksheet.mergeCells(`C3:${lastCol}3`);
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${isRTL ? 'هاتف:' : 'Tel:'} ${companyInfo?.phone || ''} | ${isRTL ? 'بريد:' : 'Email:'} ${companyInfo?.email || ''}`;
      contactCell.font = { size: 10, color: { argb: 'FF666666' } };
      contactCell.alignment = { horizontal: 'center' };

      // Separator — Row 4 (empty)
      worksheet.getRow(4).height = 6;

      // Report Title — Row 5
      worksheet.mergeCells(`A5:${lastCol}5`);
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'gradient', gradient: 'angle', degree: 90,
        stops: [
          { position: 0, color: { argb: 'FF1565C0' } },
          { position: 1, color: { argb: 'FF1976D2' } },
        ],
      };
      worksheet.getRow(5).height = 35;

      // As-of date — Row 6
      worksheet.mergeCells(`A6:${lastCol}6`);
      const dateCell = worksheet.getCell('A6');
      dateCell.value = `${isRTL ? 'حتى تاريخ:' : 'As of:'} ${formatDate(asOfDate, locale)}`;
      dateCell.font = { size: 10, bold: true };
      dateCell.alignment = { horizontal: 'center' };
      dateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };

      // Blank spacer — Row 7
      worksheet.getRow(7).height = 6;

      // Column headers — Row 8
      const headers = [
        t.assetCode, t.assetName, t.type, t.category,
        t.acquisitionDate, t.acquisitionCost,
        t.accumDepreciation, t.netBookValue,
        t.usefulLife, t.method, t.statusCol,
      ];
      const headerRow = worksheet.getRow(8);
      headers.forEach((header, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF1565C0' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'medium', color: { argb: 'FF1565C0' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        };
      });
      headerRow.height = 28;

      // Data rows — starting at Row 9
      filteredData.forEach((row, idx) => {
        const exRow = worksheet.getRow(9 + idx);
        const bgColor = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';

        exRow.getCell(1).value = row.assetCode;
        exRow.getCell(2).value = isRTL ? row.assetNameAr : row.assetName;
        exRow.getCell(3).value = assetTypeLabelEn(row.assetType);
        exRow.getCell(4).value = row.categoryName || '-';
        exRow.getCell(5).value = formatDate(row.acquisitionDate, 'en-GB');
        exRow.getCell(6).value = row.acquisitionCost;
        exRow.getCell(7).value = row.accumulatedDepreciation;
        exRow.getCell(8).value = row.netBookValue;
        exRow.getCell(9).value = `${row.usefulLifeYears} yrs`;
        exRow.getCell(10).value = methodLabelEn(row.depreciationMethod);
        exRow.getCell(11).value = statusLabelEn(row.status);

        exRow.alignment = { horizontal: 'center', vertical: 'middle' };
        exRow.height = 22;

        exRow.eachCell((cell, colNumber) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          };
          // Numeric columns: 6, 7, 8
          if (colNumber >= 6 && colNumber <= 8) {
            cell.numFmt = '#,##0.000';
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
          if (colNumber === 7) {
            cell.font = { color: { argb: 'FFC62828' } }; // red for accum dep
          }
          if (colNumber === 8) {
            cell.font = { bold: true, color: { argb: 'FF2E7D32' } }; // green for NBV
          }
        });
      });

      // Grand Total row
      const totalRowIdx = 9 + filteredData.length;
      const totalRow = worksheet.getRow(totalRowIdx);
      totalRow.getCell(1).value = isRTL ? `${t.grandTotal}` : `${t.grandTotal}`;
      totalRow.getCell(2).value = `${stats.totalAssets} ${isRTL ? 'أصل' : 'assets'}`;
      totalRow.getCell(6).value = stats.totalAcquisitionCost;
      totalRow.getCell(7).value = stats.totalAccumDepreciation;
      totalRow.getCell(8).value = stats.totalNetBookValue;

      totalRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      totalRow.alignment = { horizontal: 'center', vertical: 'middle' };
      totalRow.height = 30;

      for (let col = 1; col <= COL_COUNT; col++) {
        const cell = totalRow.getCell(col);
        cell.fill = {
          type: 'gradient', gradient: 'angle', degree: 90,
          stops: [
            { position: 0, color: { argb: 'FF0D47A1' } },
            { position: 1, color: { argb: 'FF1565C0' } },
          ],
        };
        cell.border = {
          top: { style: 'double', color: { argb: 'FF0D47A1' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'double', color: { argb: 'FF0D47A1' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        };
        if (col >= 6 && col <= 8) {
          cell.numFmt = '#,##0.000';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
      }

      // Column widths
      worksheet.columns = [
        { width: 16 }, // Asset Code
        { width: 32 }, // Asset Name
        { width: 14 }, // Type
        { width: 20 }, // Category
        { width: 16 }, // Acq Date
        { width: 18 }, // Acq Cost
        { width: 20 }, // Accum Dep
        { width: 20 }, // Net Book Value
        { width: 12 }, // Useful Life
        { width: 20 }, // Method
        { width: 18 }, // Status
      ];

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FixedAssetsRegister_${asOfDate}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const exportToPdf = async () => {
    if (!hasGenerated || filteredData.length === 0) {
      toast.warning(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Header background
      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.error('PDF: Failed to add logo:', err);
        }
      }

      // Company name
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(companyInfo?.companyName || 'Company Name', pageWidth / 2, 15, { align: 'center' });

      // Address & contact
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(
        `Tel: ${companyInfo?.phone || ''} | Email: ${companyInfo?.email || ''}`,
        pageWidth / 2, 27, { align: 'center' }
      );

      // Report title banner
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 65, 31, 130, 10, 2, 2, 'F');
      doc.text('Fixed Assets Register', pageWidth / 2, 37.5, { align: 'center' });

      // As-of date
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `As of: ${formatDate(asOfDate, 'en-GB')}`,
        pageWidth - 15, 50, { align: 'right' }
      );

      // Table
      const tableHeaders = [[
        'Asset Code', 'Asset Name', 'Type', 'Category',
        'Acq. Date', 'Acq. Cost (KWD)',
        'Accum. Dep. (KWD)', 'Net Book Value (KWD)',
        'Life', 'Method', 'Status',
      ]];

      const tableBody: (string | { content: string; styles: object })[][] = filteredData.map(row => [
        row.assetCode,
        row.assetName,
        assetTypeLabelEn(row.assetType),
        row.categoryName || '-',
        formatDate(row.acquisitionDate, 'en-GB'),
        formatNumber(row.acquisitionCost),
        formatNumber(row.accumulatedDepreciation),
        formatNumber(row.netBookValue),
        `${row.usefulLifeYears}y`,
        methodLabelEn(row.depreciationMethod),
        statusLabelEn(row.status),
      ]);

      // Grand total row
      tableBody.push([
        {
          content: 'GRAND TOTAL',
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
            halign: 'center',
          },
        },
        {
          content: `${stats.totalAssets} assets`,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
          },
        },
        { content: '', styles: { fillColor: [13, 71, 161] } },
        { content: '', styles: { fillColor: [13, 71, 161] } },
        { content: '', styles: { fillColor: [13, 71, 161] } },
        {
          content: formatNumber(stats.totalAcquisitionCost),
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'right',
          },
        },
        {
          content: formatNumber(stats.totalAccumDepreciation),
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 200, 200],
            fontStyle: 'bold',
            halign: 'right',
          },
        },
        {
          content: formatNumber(stats.totalNetBookValue),
          styles: {
            fillColor: [13, 71, 161],
            textColor: [200, 255, 200],
            fontStyle: 'bold',
            halign: 'right',
          },
        },
        { content: '', styles: { fillColor: [13, 71, 161] } },
        { content: '', styles: { fillColor: [13, 71, 161] } },
        { content: '', styles: { fillColor: [13, 71, 161] } },
      ]);

      autoTable(doc, {
        head: tableHeaders,
        body: tableBody,
        startY: 55,
        theme: 'striped',
        styles: { fontSize: 7.5, cellPadding: 2, halign: 'center', valign: 'middle' },
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center',
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },
          1: { cellWidth: 42, halign: 'left' },
          2: { cellWidth: 18, halign: 'center' },
          3: { cellWidth: 22, halign: 'left' },
          4: { cellWidth: 20, halign: 'center' },
          5: { cellWidth: 24, halign: 'right' },
          6: { cellWidth: 24, halign: 'right', textColor: [198, 40, 40] },
          7: { cellWidth: 26, halign: 'right', fontStyle: 'bold', textColor: [46, 125, 50] },
          8: { cellWidth: 12, halign: 'center' },
          9: { cellWidth: 26, halign: 'center' },
          10: { cellWidth: 18, halign: 'center' },
        },
        margin: { left: 10, right: 10 },
      });

      // Footer
      const finalY: number = (doc as any).lastAutoTable?.finalY || 200;
      if (finalY < pageHeight - 12) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Generated: ${new Date().toLocaleString('en-GB')}`,
          pageWidth / 2, pageHeight - 8, { align: 'center' }
        );
      }

      doc.save(`FixedAssetsRegister_${asOfDate}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Print section (hidden on screen) ───────────────────────────── */}
      <Box className="print-section">
        <ReportHeader
          reportTitle={t.title}
          reportTitleAr={t.titleAr}
          reportDate={formatDate(asOfDate, locale)}
        />
        {hasGenerated && (
          <TableContainer sx={{ mt: 3 }}>
            <Table size="small" sx={{
              fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
              '& th, & td': {
                border: '1px solid #1565C0',
                p: 1,
                fontSize: '11px',
                fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
              },
              '& th': { fontWeight: 700 },
            }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1565C0' }}>
                  {[t.assetCode, t.assetName, t.type, t.category, t.acquisitionDate].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                  {[t.acquisitionCost, t.accumDepreciation, t.netBookValue].map(h => (
                    <TableCell key={h} align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                  {[t.usefulLife, t.method, t.statusCol].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row, idx) => (
                  <TableRow key={row.fixedAssetId} sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'action.hover' }}>
                    <TableCell>{row.assetCode}</TableCell>
                    <TableCell>{isRTL ? row.assetNameAr : row.assetName}</TableCell>
                    <TableCell>{assetTypeLabel(row.assetType)}</TableCell>
                    <TableCell>{row.categoryName || '-'}</TableCell>
                    <TableCell>{formatDate(row.acquisitionDate, locale)}</TableCell>
                    <TableCell align="right">{formatNumber(row.acquisitionCost)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{formatNumber(row.accumulatedDepreciation)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatNumber(row.netBookValue)}</TableCell>
                    <TableCell>{row.usefulLifeYears} {t.years}</TableCell>
                    <TableCell>{methodLabel(row.depreciationMethod)}</TableCell>
                    <TableCell>{statusLabel(row.status)}</TableCell>
                  </TableRow>
                ))}
                {/* Grand total */}
                <TableRow sx={{ bgcolor: '#1565C0' }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>
                    {t.grandTotal} ({stats.totalAssets})
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>{formatNumber(stats.totalAcquisitionCost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffcdd2' }}>{formatNumber(stats.totalAccumDepreciation)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c8e6c9' }}>{formatNumber(stats.totalNetBookValue)}</TableCell>
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Screen section ─────────────────────────────────────────────── */}

      {/* Page header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2, display: 'flex' }}>
          <AssetsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary">
            {isRTL ? t.titleAr : t.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
      </Box>
      <Divider className="no-print" sx={{ mb: 3 }} />

      {/* Summary stat cards — shown only after generating */}
      {hasGenerated && (
        <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.totalAssets}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.totalAssets}</Typography>
                </Box>
                <AssetsIcon sx={{ fontSize: 48, opacity: 0.2, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{formatNumber(stats.totalAcquisitionCost)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.totalAcquisitionCost} ({t.kwd})</Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, opacity: 0.2, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatNumber(stats.totalAccumDepreciation)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t.totalAccumDepreciation} ({t.kwd})</Typography>
                </Box>
                <DepIcon sx={{ fontSize: 48, opacity: 0.2, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatNumber(stats.totalNetBookValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t.totalNetBookValue} ({t.kwd})</Typography>
                </Box>
                <NBVIcon sx={{ fontSize: 48, opacity: 0.2, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters bar */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            {t.filters}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* As of Date */}
          <TextField
            type="date"
            size="small"
            label={t.asOfDate}
            value={asOfDate}
            onChange={e => setAsOfDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />

          {/* Asset Type */}
          <TextField
            select
            size="small"
            label={t.assetType}
            value={assetTypeFilter}
            onChange={e => { setAssetTypeFilter(e.target.value as AssetType | ''); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t.all}</MenuItem>
            <MenuItem value="Tangible">{t.tangible}</MenuItem>
            <MenuItem value="Intangible">{t.intangible}</MenuItem>
          </TextField>

          {/* Status */}
          <TextField
            select
            size="small"
            label={t.status}
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as AssetStatus | ''); setPage(0); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t.all}</MenuItem>
            <MenuItem value="Active">{t.active}</MenuItem>
            <MenuItem value="FullyDepreciated">{t.fullyDepreciated}</MenuItem>
            <MenuItem value="Disposed">{t.disposed}</MenuItem>
          </TextField>

          {/* Generate */}
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
            onClick={handleGenerate}
            disabled={loading}
          >
            {t.generateReport}
          </Button>

          {/* Export buttons — only visible after generate */}
          {hasGenerated && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ExcelIcon />}
                onClick={exportToExcel}
                disabled={loading}
              >
                {t.exportExcel}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<PdfIcon />}
                onClick={exportToPdf}
                disabled={loading}
              >
                {t.exportPdf}
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Data table */}
      <TableContainer component={Paper} className="no-print" sx={{ mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : !hasGenerated ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6, gap: 2 }}>
            <AssetsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography color="text.secondary">{t.noData}</Typography>
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <Typography color="text.secondary">{t.noData}</Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDark ? 0.3 : 0.1) }}>
                  <TableCell><strong>{t.assetCode}</strong></TableCell>
                  <TableCell><strong>{t.assetName}</strong></TableCell>
                  <TableCell><strong>{t.type}</strong></TableCell>
                  <TableCell><strong>{t.category}</strong></TableCell>
                  <TableCell><strong>{t.acquisitionDate}</strong></TableCell>
                  <TableCell align="right"><strong>{t.acquisitionCost}</strong></TableCell>
                  <TableCell align="right"><strong>{t.accumDepreciation}</strong></TableCell>
                  <TableCell align="right"><strong>{t.netBookValue}</strong></TableCell>
                  <TableCell><strong>{t.usefulLife}</strong></TableCell>
                  <TableCell><strong>{t.method}</strong></TableCell>
                  <TableCell><strong>{t.statusCol}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedData.map((row, idx) => (
                  <TableRow
                    key={row.fixedAssetId}
                    hover
                    sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : alpha(theme.palette.action.hover, 0.04) }}
                  >
                    {/* Asset Code */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {row.assetCode}
                      </Typography>
                    </TableCell>

                    {/* Asset Name */}
                    <TableCell>
                      {isRTL ? row.assetNameAr : row.assetName}
                    </TableCell>

                    {/* Type chip */}
                    <TableCell>
                      <Chip
                        label={assetTypeLabel(row.assetType)}
                        size="small"
                        color={typeColor(row.assetType)}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.categoryName || '-'}
                      </Typography>
                    </TableCell>

                    {/* Acquisition Date */}
                    <TableCell>{formatDate(row.acquisitionDate, locale)}</TableCell>

                    {/* Acquisition Cost */}
                    <TableCell align="right">
                      <Typography variant="body2">{formatNumber(row.acquisitionCost)}</Typography>
                    </TableCell>

                    {/* Accumulated Depreciation */}
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main">
                        {formatNumber(row.accumulatedDepreciation)}
                      </Typography>
                    </TableCell>

                    {/* Net Book Value */}
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={row.netBookValue > 0 ? 'success.main' : 'text.secondary'}
                      >
                        {formatNumber(row.netBookValue)}
                      </Typography>
                    </TableCell>

                    {/* Useful Life */}
                    <TableCell>
                      <Typography variant="body2">
                        {row.usefulLifeYears} {t.years}
                      </Typography>
                    </TableCell>

                    {/* Method */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {methodLabel(row.depreciationMethod)}
                      </Typography>
                    </TableCell>

                    {/* Status chip */}
                    <TableCell>
                      <Chip
                        label={statusLabel(row.status)}
                        size="small"
                        color={statusColor(row.status)}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {/* Grand total row */}
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDark ? 0.4 : 0.12) }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {t.grandTotal} &mdash; {stats.totalAssets} {isRTL ? 'أصل' : 'assets'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(stats.totalAcquisitionCost)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="error.main">
                      {formatNumber(stats.totalAccumDepreciation)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="success.main">
                      {formatNumber(stats.totalNetBookValue)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t.rowsPerPage}
            />
          </>
        )}
      </TableContainer>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          body * {
            visibility: hidden;
            font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
          }
          .print-section,
          .print-section * {
            visibility: visible !important;
          }
          .no-print {
            display: none !important;
          }
          .print-section {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
        @media screen {
          .print-section {
            display: none;
          }
        }
      `}</style>
    </Box>
  );
};

export default FixedAssetsReportPage;
